// src/ui/dom.ts
// A ~60-line element helper instead of a framework.
//
// The brief asked for "no framework you cannot explain". This is the whole rendering layer: `el()`
// builds a node, `mount()` replaces a container's children, and screens are functions that return
// nodes. There is no virtual DOM, no reactivity and no lifecycle, because a six-screen app that
// re-renders a whole screen on change does not need any of it.
//
// EVERYTHING GOES IN AS `textContent`, NEVER `innerHTML`. Every string this app renders came from
// the server — a coach's note, a workload sentence, a kill-switch message. `innerHTML` on any of
// those is a stored-XSS hole, and the values most likely to carry markup are exactly the free-text
// ones (notes, comments, error messages). The type of `el`'s children makes the safe path the only
// path: you cannot pass HTML.

type Child = Node | string | number | null | undefined | false;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number | boolean | ((e: Event) => void) | undefined | null> = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === null || v === false) continue;
    if (k.startsWith('on') && typeof v === 'function') {
      node.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
    } else if (k === 'class') {
      node.className = String(v);
    } else if (v === true) {
      node.setAttribute(k, '');
    } else {
      node.setAttribute(k, String(v));
    }
  }
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    node.append(typeof c === 'object' ? c : document.createTextNode(String(c)));
  }
  return node;
}

export function mount(container: HTMLElement, ...nodes: Child[]): void {
  container.replaceChildren();
  for (const n of nodes) {
    if (n === null || n === undefined || n === false) continue;
    container.append(typeof n === 'object' ? n : document.createTextNode(String(n)));
  }
}

/** A labelled block of server-supplied text. The label is ours; the text is always theirs. */
export function field(label: string, value: string | number | null | undefined): HTMLElement {
  return el('div', { class: 'field' },
    el('span', { class: 'field-label' }, label),
    el('span', { class: 'field-value' }, value === null || value === undefined || value === '' ? '—' : value));
}

export function section(title: string, ...children: Child[]): HTMLElement {
  return el('section', { class: 'card' }, el('h2', {}, title), ...children);
}

/**
 * An explanation, COLLAPSED.
 *
 * ── WHY THIS EXISTS (27 Aug 2026) ─────────────────────────────────────────────────────────────
 * Every screen in this app opened with a paragraph explaining its own honesty rules — why a count
 * is not merged, why an id is typed, why nothing is inferred locally. Those paragraphs are true
 * and worth keeping: they are the reason the numbers underneath can be trusted.
 *
 * They were also the first thing between a coach and the thing they came to do. The founder's
 * verdict on seeing it on a phone was blunt and correct: it read as a diagnostic console, not a
 * product. Four lines of rationale sat above the input on a screen whose whole job was to accept
 * one id.
 *
 * So the text is not deleted — deleting it would remove the audit trail that makes this product
 * different. It is folded behind a one-line summary, closed by default. Anyone who wants to know
 * why the app behaves as it does is one tap away; everyone else sees the work.
 *
 * Use `why()` for RATIONALE. Keep `class: 'note'` for anything that changes what the reader does
 * next — a warning, a state, an instruction. The test of which is: if it were hidden and somebody
 * acted differently as a result, it is not rationale and must stay visible.
 */
export function why(...paragraphs: string[]): HTMLElement {
  return el('details', { class: 'why' },
    el('summary', {}, 'Why this works the way it does'),
    ...paragraphs.map((t) => el('p', { class: 'note' }, t)));
}

/**
 * The empty state. Takes the REASON, not a decoration.
 *
 * There is no default text. A screen with nothing to show must say WHY it has nothing — "no sessions
 * recorded yet" and "you are not allowed to see this athlete's sessions" are different facts, and a
 * shared "Nothing here!" would erase the difference.
 */
export function empty(reason: string): HTMLElement {
  return el('p', { class: 'empty' }, reason);
}

export function spinner(label: string): HTMLElement {
  return el('p', { class: 'loading' }, label);
}
